import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, MapPin, Car, User, Phone, Loader2, Calendar, Coffee } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAMPAGNE_OPTS = ["Avec alcool", "Sans alcool", "Non"];

export default function ConfirmationMariage() {
  const { token } = useParams();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [form, setForm] = useState({
    adresse_depart: "",
    heure_depart: "",
    adresse_mairie: "",
    horaire_mairie: "",
    adresse_eglise: "",
    horaire_eglise: "",
    theme_decoration: "",
    champagne: "",
  });

  useEffect(() => {
    axios.get(`${API}/confirm/${token}`)
      .then(r => {
        setDevis(r.data);
        setForm({
          adresse_depart: r.data.adresse_depart || "",
          heure_depart: r.data.heure_depart || "",
          adresse_mairie: r.data.adresse_mairie || "",
          horaire_mairie: r.data.horaire_mairie || "",
          adresse_eglise: r.data.adresse_eglise || "",
          horaire_eglise: r.data.horaire_eglise || "",
          theme_decoration: r.data.theme_decoration || "",
          champagne: r.data.champagne || "",
        });
        if (r.data.confirmed_at) setConfirmed(true);
      })
      .catch(() => setError("Lien invalide ou expiré. Veuillez contacter RM Location Prestige."))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError("");
    try {
      await axios.post(`${API}/confirm/${token}`, form);
      setConfirmed(true);
    } catch (err) {
      setSaveError(err.response?.data?.detail || "Erreur lors de la confirmation");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center">
      <Loader2 size={32} className="animate-spin text-[#D4A843]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-6">
      <div className="bg-white p-8 max-w-md w-full text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-red-500 text-2xl">✕</span>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h2>
        <p className="text-gray-500 text-sm">{error}</p>
        <p className="text-sm text-gray-400 mt-4">Tél : 0692 04 20 73</p>
      </div>
    </div>
  );

  if (confirmed) return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-6">
      <div className="bg-white p-8 max-w-md w-full text-center shadow-2xl">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Informations confirmées !</h2>
        <p className="text-gray-500 text-sm mb-4">
          Les informations du mariage de <strong>{devis?.client_name}</strong> ont été confirmées.
        </p>
        <div className="bg-[#f9f7f3] p-4 text-left text-sm space-y-2 border-l-4 border-[#D4A843]">
          <p><strong>Date :</strong> {devis?.date_mariage}</p>
          <p><strong>Véhicule :</strong> {devis?.vehicle_name}</p>
          {form.heure_depart && <p><strong>Heure départ :</strong> {form.heure_depart}</p>}
          {form.adresse_depart && <p><strong>Adresse départ :</strong> {form.adresse_depart}</p>}
        </div>
        <p className="text-xs text-gray-400 mt-6">Les emails ont été envoyés à l'équipe. Merci !</p>
        <p className="text-sm font-semibold text-[#D4A843] mt-2">RM Location Prestige</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#1a1a1a] border-b border-gray-800 px-6 py-5 text-center">
        <h1 className="text-white font-bold text-xl">RM LOCATION PRESTIGE</h1>
        <p className="text-[#D4A843] text-xs tracking-widest uppercase mt-0.5">Confirmation de mariage</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Wedding info card */}
        <div className="bg-white shadow-2xl mb-6">
          <div className="bg-[#D4A843] px-6 py-4">
            <h2 className="text-white font-bold text-lg">Mariage du {devis?.date_mariage}</h2>
            <p className="text-white/80 text-sm">{devis?.numero}</p>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <User size={16} className="text-[#D4A843] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Client</p>
                <p className="font-semibold text-gray-900 text-sm">{devis?.client_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={16} className="text-[#D4A843] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Téléphone</p>
                <p className="font-semibold text-gray-900 text-sm">{devis?.client_telephone}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Car size={16} className="text-[#D4A843] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Véhicule</p>
                <p className="font-semibold text-gray-900 text-sm">{devis?.vehicle_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Coffee size={16} className="text-[#D4A843] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Champagne</p>
                <p className="font-semibold text-gray-900 text-sm">{devis?.champagne || "Non renseigné"}</p>
              </div>
            </div>
            {devis?.lieu_evenement && (
              <div className="flex items-start gap-3 col-span-2">
                <MapPin size={16} className="text-[#D4A843] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Lieu de réception</p>
                  <p className="font-semibold text-gray-900 text-sm">{devis.lieu_evenement}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation form */}
        <div className="bg-white shadow-2xl">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900">Confirmer / Compléter les informations logistiques</h3>
            <p className="text-xs text-gray-400 mt-0.5">Ces informations seront transmises au chauffeur et à la décoratrice</p>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {saveError && <p className="text-red-500 text-sm bg-red-50 p-3 border border-red-200">{saveError}</p>}

            {/* Départ */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Clock size={13} /> Départ
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Adresse de départ</label>
                  <input type="text" value={form.adresse_depart} onChange={e => set("adresse_depart", e.target.value)}
                    data-testid="confirm-adresse-depart"
                    placeholder="Ex: 12 Rue des Fleurs, Saint-Denis"
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Heure de départ</label>
                  <input type="time" value={form.heure_depart} onChange={e => set("heure_depart", e.target.value)}
                    data-testid="confirm-heure-depart"
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
              </div>
            </div>

            {/* Mairie */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Calendar size={13} /> Mairie
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Adresse de la mairie</label>
                  <input type="text" value={form.adresse_mairie} onChange={e => set("adresse_mairie", e.target.value)}
                    data-testid="confirm-adresse-mairie"
                    placeholder="Ex: Mairie de Saint-Denis"
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Horaire mairie</label>
                  <input type="time" value={form.horaire_mairie} onChange={e => set("horaire_mairie", e.target.value)}
                    data-testid="confirm-horaire-mairie"
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
              </div>
            </div>

            {/* Église */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <MapPin size={13} /> Église / Cérémonie
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Adresse de l'église</label>
                  <input type="text" value={form.adresse_eglise} onChange={e => set("adresse_eglise", e.target.value)}
                    data-testid="confirm-adresse-eglise"
                    placeholder="Ex: Cathédrale Saint-Denis"
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Horaire cérémonie</label>
                  <input type="time" value={form.horaire_eglise} onChange={e => set("horaire_eglise", e.target.value)}
                    data-testid="confirm-horaire-eglise"
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
              </div>
            </div>

            {/* Décoration */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Décoration</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Thème décoration</label>
                  <input type="text" value={form.theme_decoration} onChange={e => set("theme_decoration", e.target.value)}
                    data-testid="confirm-theme"
                    placeholder="Ex: Romantique, Champêtre..."
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1 font-medium">Champagne</label>
                  <select value={form.champagne} onChange={e => set("champagne", e.target.value)}
                    data-testid="confirm-champagne"
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
                    <option value="">-- Sélectionner --</option>
                    {CHAMPAGNE_OPTS.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <button type="submit" disabled={saving} data-testid="confirm-submit-btn"
                className="w-full py-3.5 bg-[#D4A843] text-white font-bold text-base hover:bg-[#c49933] disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                {saving ? "Confirmation en cours..." : "Confirmer les informations"}
              </button>
              <p className="text-center text-xs text-gray-400 mt-3">
                En confirmant, un email sera automatiquement envoyé au chauffeur et à la décoratrice.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
